import * as mqtt from "mqtt";

import packageJson from "../../../package.json";
import {
  BrowserInfo,
  Capabilities,
  ClientHints,
  DeviceInfo,
  DeviceProfileReport,
  DisplayInfo,
} from "types/analytics";
import { SESSION_ID, SESSION_START } from "./session";
import { getCapabilities } from "./capabilities";
import { chromiumVersionFromHints } from "./client-hints";
import { estimateChromiumVersion } from "./version-estimate";

/**
 * Builds the one-off device profile: who the plotter is, what its screen looks
 * like and which web platform features it supports.
 */

/** Marine display vendors that tend to leave a token in the user agent */
const PLOTTER_VENDORS = [
  "garmin",
  "raymarine",
  "furuno",
  "simrad",
  "navico",
  "lowrance",
  "humminbird",
  "b&g",
  "beijer",
  "compulab",
  "advantech",
  "dfi",
];

const firstMatch = (value: string, pattern: RegExp): string | null => {
  const match = value.match(pattern);
  return match && match[1] ? match[1] : null;
};

const majorVersion = (version: string | null): number | null => {
  if (!version) return null;
  const parsed = parseInt(version.split(".")[0], 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Detects a Chromium engine without reading the user agent, for plotters whose
 * WebView reports a rewritten or model-only UA string.
 *
 * `Error.captureStackTrace` is V8-only and has been there since well before the
 * oldest browser we support, which makes it the most dependable single marker.
 */
const detectEngine = () => {
  const nav: any = window?.navigator ?? {};

  const isV8 = typeof (Error as any).captureStackTrace === "function";

  // Blink-only prefixed APIs, still present in current Chromium
  const isBlink =
    typeof (window as any).chrome !== "undefined" ||
    typeof (document as any).webkitFullscreenEnabled !== "undefined" ||
    typeof (window as any).webkitRequestFileSystem !== "undefined" ||
    typeof (nav as any).webkitPersistentStorage !== "undefined";

  const brands: any[] = nav.userAgentData?.brands ?? [];
  const brandsSayChromium = brands.some((entry) =>
    /chromium|google chrome/i.test(entry?.brand ?? "")
  );

  return {
    is_v8: isV8,
    is_blink: isBlink,
    // V8 plus Blink is Chromium; the brand list is decisive when present
    is_chromium: brandsSayChromium || (isV8 && isBlink),
  };
};

const getBrowserInfo = (
  userAgent: string,
  hints: ClientHints | null,
  capabilities: Capabilities
): BrowserInfo => {
  const userAgentChromiumVersion = firstMatch(
    userAgent,
    /(?:Chrome|Chromium|CriOS|HeadlessChrome)\/(\d+[\d.]*)/
  );

  // Client hints carry the real version even when the UA is frozen or rewritten
  const hintsChromiumVersion = chromiumVersionFromHints(hints);
  const chromiumVersion = hintsChromiumVersion ?? userAgentChromiumVersion;
  const chromiumVersionSource = hintsChromiumVersion
    ? "client_hints"
    : userAgentChromiumVersion
    ? "user_agent"
    : null;

  // Order matters: Chromium-based browsers all claim to be Chrome
  const name = /Edge?\//.test(userAgent)
    ? "edge"
    : /OPR\/|Opera/.test(userAgent)
    ? "opera"
    : /QtWebEngine/.test(userAgent)
    ? "qtwebengine"
    : /Chromium\//.test(userAgent)
    ? "chromium"
    : chromiumVersion
    ? "chrome"
    : /Firefox\//.test(userAgent)
    ? "firefox"
    : /Safari\//.test(userAgent)
    ? "safari"
    : null;

  const version =
    chromiumVersion ??
    firstMatch(userAgent, /(?:Firefox|Version|QtWebEngine)\/(\d+[\d.]*)/);

  return {
    name,
    version,
    major_version: majorVersion(version),
    chromium_version: chromiumVersion,
    chromium_major_version: majorVersion(chromiumVersion),
    chromium_version_source: chromiumVersionSource,
    ...detectEngine(),
    webkit_version: firstMatch(userAgent, /AppleWebKit\/(\d+[\d.]*)/),
    // Android WebViews carry a "wv" token; older ones pair "Version/x" with
    // "Chrome/x" instead
    is_webview:
      /;\s*wv\)/.test(userAgent) ||
      (/Version\/\d/.test(userAgent) && /Chrome\/\d/.test(userAgent)) ||
      /QtWebEngine/.test(userAgent),
    qt_webengine_version: firstMatch(userAgent, /QtWebEngine\/(\d+[\d.]*)/),
    version_estimate: estimateChromiumVersion(capabilities),
  };
};

const getOsInfo = (userAgent: string, platform: string | null) => {
  const androidVersion = firstMatch(userAgent, /Android\s+([\d.]+)/);
  if (androidVersion || /Android/.test(userAgent)) {
    return { os: "android", os_version: androidVersion };
  }

  const windowsVersion = firstMatch(userAgent, /Windows NT\s+([\d.]+)/);
  if (windowsVersion) return { os: "windows", os_version: windowsVersion };

  const macVersion = firstMatch(userAgent, /Mac OS X\s+([\d_.]+)/);
  if (macVersion) {
    return { os: "macos", os_version: macVersion.replace(/_/g, ".") };
  }

  if (/CrOS/.test(userAgent)) {
    return {
      os: "chromeos",
      os_version: firstMatch(userAgent, /CrOS \S+ (\S+)/),
    };
  }

  if (/Linux/.test(userAgent)) return { os: "linux", os_version: null };

  return { os: platform ? platform.toLowerCase() : null, os_version: null };
};

/**
 * Android-style UAs put the model between the OS version and the build token,
 * e.g. "Linux; Android 9; SM-G950F Build/PPR1". Desktop-class UAs have no model,
 * in which case this returns null.
 */
const getHardwareModel = (userAgent: string): string | null => {
  const model =
    firstMatch(userAgent, /Android[\s\d.]*;\s*([^;)]+?)\s*(?:Build\/|[;)])/) ??
    firstMatch(userAgent, /\bModel\/([^;)\s]+)/);

  if (!model) return null;

  const cleaned = model.trim();
  // "wv" and bare locale codes are not models
  return cleaned && cleaned !== "wv" && !/^[a-z]{2}(-[a-z]{2})?$/i.test(cleaned)
    ? cleaned
    : null;
};

const getPlotterVendorHint = (userAgent: string): string | null => {
  const lowered = userAgent.toLowerCase();
  for (let i = 0; i < PLOTTER_VENDORS.length; i++) {
    if (lowered.indexOf(PLOTTER_VENDORS[i]) !== -1) return PLOTTER_VENDORS[i];
  }
  return null;
};

const getDeviceInfo = (
  hints: ClientHints | null,
  capabilities: Capabilities
): DeviceInfo => {
  const nav: any = window?.navigator ?? {};
  const userAgent: string = nav.userAgent ?? "";
  const platform: string | null = nav.platform ?? null;
  const browser = getBrowserInfo(userAgent, hints, capabilities);
  const { os, os_version } = getOsInfo(userAgent, platform);

  return {
    user_agent: userAgent || null,
    // A UA that carries no engine token at all tells us nothing we can act on
    user_agent_reliable:
      !!userAgent && !!(browser.name || browser.webkit_version),
    browser,
    client_hints: hints,
    platform,
    vendor: nav.vendor ?? null,
    os,
    // Kept purely user-agent derived so each field has a single source; the
    // client hints' own platform version is published under `client_hints`
    os_version,
    // Client hints expose the model directly and are authoritative when present;
    // the UA string only sometimes carries one
    hardware_model: hints?.model ?? getHardwareModel(userAgent),
    plotter_vendor_hint: getPlotterVendorHint(userAgent),
    hardware_concurrency:
      typeof nav.hardwareConcurrency === "number"
        ? nav.hardwareConcurrency
        : null,
    device_memory_gb:
      typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    max_touch_points:
      typeof nav.maxTouchPoints === "number" ? nav.maxTouchPoints : null,
    touch_events: "ontouchstart" in window,
    pointer_events: typeof (window as any).PointerEvent !== "undefined",
    language: nav.language ?? null,
    languages: nav.languages ? Array.prototype.slice.call(nav.languages) : null,
    timezone: getTimezone(),
    online: typeof nav.onLine === "boolean" ? nav.onLine : null,
  };
};

const getTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch (e) {
    return null;
  }
};

const getOrientation = (): string | null => {
  try {
    const orientation: any = (window.screen as any)?.orientation;
    if (orientation?.type) return orientation.type;

    if (typeof window.matchMedia === "function") {
      return window.matchMedia("(orientation: portrait)").matches
        ? "portrait"
        : "landscape";
    }
  } catch (e) {
    // ignore
  }
  return null;
};

export const getDisplayInfo = (): DisplayInfo => {
  const screen: any = window?.screen ?? {};
  const pixelRatio =
    typeof window?.devicePixelRatio === "number"
      ? window.devicePixelRatio
      : null;
  const screenWidth = typeof screen.width === "number" ? screen.width : null;
  const screenHeight = typeof screen.height === "number" ? screen.height : null;
  const doc = document?.documentElement;

  return {
    screen_width: screenWidth,
    screen_height: screenHeight,
    avail_width:
      typeof screen.availWidth === "number" ? screen.availWidth : null,
    avail_height:
      typeof screen.availHeight === "number" ? screen.availHeight : null,
    viewport_width: window?.innerWidth ?? null,
    viewport_height: window?.innerHeight ?? null,
    document_width: doc?.clientWidth ?? null,
    document_height: doc?.clientHeight ?? null,
    device_pixel_ratio: pixelRatio,
    effective_width:
      screenWidth !== null && pixelRatio !== null
        ? Math.round(screenWidth * pixelRatio)
        : screenWidth,
    effective_height:
      screenHeight !== null && pixelRatio !== null
        ? Math.round(screenHeight * pixelRatio)
        : screenHeight,
    color_depth:
      typeof screen.colorDepth === "number" ? screen.colorDepth : null,
    orientation: getOrientation(),
  };
};

export const buildDeviceProfile = (
  client: mqtt.MqttClient | null,
  browserSpeed: number | null,
  hints: ClientHints | null,
  deviceId: string | null,
  vesselId: string | null
): DeviceProfileReport => {
  // Capabilities are built first: the feature probes also drive the
  // user-agent-independent Chromium version estimate
  const capabilities = getCapabilities(client, browserSpeed);

  return {
    type: "profile",
    session_id: SESSION_ID,
    device_id: deviceId,
    vessel_id: vesselId,
    software_version: packageJson.version,
    timestamp: new Date().toISOString(),
    session_start_timestamp: SESSION_START.toISOString(),
    device: getDeviceInfo(hints, capabilities),
    display: getDisplayInfo(),
    capabilities,
  };
};
